import { HttpErrorResponse, httpResource } from '@angular/common/http';
import { computed, inject, Injectable, Signal, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { finalize } from 'rxjs';

import { DemandsDateRangeChange } from '../../../Catalog/Interfaces/demands-filter-panel.interface';
import { MsgService } from '../../../Shared/Services/msg.service';
import { ResourceStateService } from '../../../Shared/Services/resource-state.service';
import { resolveBlobFailureKey } from '../../Demand/Shared/report-blob-error.util';
import { saveBlobAsFile } from '../../Demand/Shared/report-file.util';
import {
  MONITORING_EMPTY_APPROVAL,
  MONITORING_EMPTY_BY_KIND,
  MONITORING_EMPTY_BY_STATUS,
  MONITORING_EMPTY_CATALOG,
  MONITORING_EMPTY_PERIODS,
  MONITORING_EMPTY_DEMANDS,
  MONITORING_EMPTY_LIFETIME,
  MONITORING_EMPTY_TOP,
} from '../Consts/monitoring-empty.const';
import {
  MONITORING_EXPORT_KEYS,
  MONITORING_EXPORT_TOO_MANY_REQUESTS,
} from '../Consts/monitoring-export.const';
import {
  MonitoringApproval,
  MonitoringByKind,
  MonitoringByStatus,
  MonitoringCatalog,
  MonitoringPeriods,
  MonitoringDemands,
  MonitoringLifetime,
  MonitoringReport,
  MonitoringTop,
} from '../Interfaces/monitoring-report.interface';
import { MonitoringService } from '../Services/monitoring.service';
import { MonitoringResponse } from '../Types/monitoring-response.type';
import { buildDefaultMonitoringRange } from '../Shared/monitoring-default-range.util';
import { monitoringExportFallbackFilename } from '../Shared/monitoring-export-filename.util';

@Injectable()
export class MonitoringFacade {
  private readonly _monitoring = inject(MonitoringService);
  private readonly _resourceState = inject(ResourceStateService);
  private readonly _msg = inject(MsgService);
  private readonly _translate = inject(TranslateService);

  private readonly _initialRange = buildDefaultMonitoringRange();
  private readonly _from = signal<string | null>(this._initialRange.from);
  private readonly _to = signal<string | null>(this._initialRange.to);
  private readonly _exporting = signal(false);

  private readonly _report = httpResource<MonitoringResponse>(() =>
    this._monitoring.show({ from: this._from(), to: this._to() }),
  );

  readonly from: Signal<string | null> = this._from.asReadonly();
  readonly to: Signal<string | null> = this._to.asReadonly();
  readonly exporting: Signal<boolean> = this._exporting.asReadonly();

  readonly periods: Signal<MonitoringPeriods> = this._blockOf('periods', MONITORING_EMPTY_PERIODS);

  readonly catalog: Signal<MonitoringCatalog> = this._blockOf('catalog', MONITORING_EMPTY_CATALOG);
  readonly lifetime: Signal<MonitoringLifetime> = this._blockOf(
    'lifetime',
    MONITORING_EMPTY_LIFETIME,
  );
  readonly demands: Signal<MonitoringDemands> = this._blockOf('demands', MONITORING_EMPTY_DEMANDS);
  readonly approval: Signal<MonitoringApproval> = this._blockOf(
    'approval',
    MONITORING_EMPTY_APPROVAL,
  );

  readonly byKind: Signal<MonitoringByKind> = this._blockOf('by_kind', MONITORING_EMPTY_BY_KIND);
  readonly byStatus: Signal<MonitoringByStatus> = this._blockOf(
    'by_status',
    MONITORING_EMPTY_BY_STATUS,
  );
  readonly top: Signal<MonitoringTop> = this._blockOf('top', MONITORING_EMPTY_TOP);

  readonly coldLoading: Signal<boolean> = computed(
    () =>
      this._report.status() === 'loading' &&
      this._resourceState.valueOf(this._report) === undefined,
  );

  readonly hasData: Signal<boolean> = computed(
    () => this._resourceState.valueOf(this._report) !== undefined,
  );

  readonly loading: Signal<boolean> = computed(() => this._report.isLoading());

  readonly hasError: Signal<boolean> = computed(() => this._report.error() !== undefined);

  initialise(): void {
    this._applyRange(buildDefaultMonitoringRange());
    this._report.reload();
  }

  reload(): void {
    this._report.reload();
  }

  setRange(range: DemandsDateRangeChange): void {
    this._applyRange(range);
  }

  exportWorkbook(): void {
    if (this._exporting()) return;

    this._exporting.set(true);
    this._monitoring
      .exportWorkbook({ from: this._from(), to: this._to() })
      .pipe(finalize(() => this._exporting.set(false)))
      .subscribe({
        next: ({ blob, filename }) =>
          saveBlobAsFile(blob, filename ?? monitoringExportFallbackFilename()),
        error: (error: unknown) => void this._announceExportFailure(error),
      });
  }

  private _applyRange(range: DemandsDateRangeChange): void {
    this._from.set(range.from);
    this._to.set(range.to);
  }

  private async _announceExportFailure(error: unknown): Promise<void> {
    const rateLimited =
      error instanceof HttpErrorResponse && error.status === MONITORING_EXPORT_TOO_MANY_REQUESTS;
    const key = rateLimited
      ? MONITORING_EXPORT_KEYS.rateLimited
      : await resolveBlobFailureKey(error, MONITORING_EXPORT_KEYS.failed, this._translate);

    this._msg.showSnackBar(key, { type: 'error' });
  }

  private _blockOf<K extends keyof MonitoringReport>(
    key: K,
    empty: MonitoringReport[K],
  ): Signal<MonitoringReport[K]> {
    return computed(() => this._resourceState.valueOf(this._report)?.data?.[key] ?? empty);
  }
}
